import * as React from 'react'
import * as PropTypes from 'prop-types'
import moment from 'moment'
import { css, Rule } from 'glamor'

import { UiTheme, IWithThemeProps, withTheme } from '../../theme'

import { Touchable } from '../touchable'
import { Flex } from '../flex'
import { StyledText } from '../styled-text'

import { DatepickerDate } from './datepicker-date'
import ArrowRight from './arrow-right.svg'
import DoubleArrow from './double-arrow.svg'

import { getFullMonth, getFullYear, getFullDate, range } from './utils'

const getStyles = (theme: UiTheme) => {
  const dates = {
    display: 'inline-grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
  }
  const months = {
    display: 'inline-grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
  }
  const monthName = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    margin: '0 10px',
    padding: '5px 0',
    textAlign: 'center',
    border: theme.settings.borders.regular,
    borderRadius: theme.settings.spacing.medium,
    height: theme.settings.spacing.xLarge,
    width: '100%',
    color: theme.colors.text.normal,
    '.intent-mouse &:focus': {
      outline: 'none',
    },
    ':active': {
      backgroundColor: theme.colors.backgrounds.gray,
      borderColor: 'transparent',
    },
  }
  const monthNameInner = {
    width: '100%',
  }
  const monthNameDoubleArrow = {
    marginLeft: '-20px',
    marginRight: '10px',
  }
  const arrow = {
    padding: theme.settings.spacing.small,
    minWidth: '0',
    cursor: 'pointer',
    '& path': { fill: theme.colors.accents.primary },
    ':hover path': { fill: theme.colors.accents.primaryHover },
    '.intent-mouse &:focus': {
      outline: 'none',
    },
  }
  const arrowDisabled = {
    cursor: 'default',
    '& path': { fill: 'default' },
    ':hover path': { fill: 'default' },
  }
  const title = {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'center',
    lineHeight: '20px',
    textAlign: 'center',
    paddingLeft: '0.5em',
    fontWeight: '600',
  }
  const dateClass = {
    cursor: 'pointer',
    color: theme.colors.accents.primary,
    transition: 'all 0.15s cubic-bezier(0.23, 1, 0.32, 1)',
  }

  return {
    dates,
    months,
    monthName,
    monthNameInner,
    monthNameDoubleArrow,
    arrow,
    arrowDisabled,
    title,
    dateClass,
  }
}

function isEmptyPeriod(props: IDatepickerMonthProps) {
  return !props.timeperiod.toDate && !props.timeperiod.fromDate
}

function getFirstDate({ isFromMonth, timeperiod: { fromDate, toDate } }: IDatepickerMonthProps) {
  if (isFromMonth) {
    return moment(fromDate || toDate || undefined).startOf('month')
  }
  return moment(toDate || fromDate || undefined).startOf('month')
}

interface IDatepickerMonthProps {
  timeperiod: {
    fromDate?: moment.Moment | null
    toDate?: moment.Moment | null
  }
  date?: moment.Moment
  locale: string
  onDateSelected: (date: moment.Moment) => void
  firstSelectableDate?: moment.Moment
  lastSelectableDate?: moment.Moment
  style?: Rule
  isFromMonth?: boolean
  isSingleMonth?: boolean
  title?: React.ReactNode
}

interface IDatepickerMonthState {
  firstDate: moment.Moment
  view: 'month' | 'year'
}

export const DatepickerMonth = withTheme(
  class DatepickerMonth extends React.Component<IDatepickerMonthProps & IWithThemeProps, IDatepickerMonthState> {
    static propTypes = {
      timeperiod: PropTypes.object.isRequired,
      locale: PropTypes.string.isRequired,
      onDateSelected: PropTypes.func.isRequired,
      firstSelectableDate: PropTypes.object as any,
      lastSelectableDate: PropTypes.object as any,
      style: PropTypes.object,
      isFromMonth: PropTypes.bool,
      isSingleMonth: PropTypes.bool,
      title: PropTypes.element,
    }

    static defaultProps = {
      isFromMonth: false,
      isSingleMonth: false,
    }

    state: IDatepickerMonthState = {
      firstDate: getFirstDate(this.props),
      view: 'month',
    }

    onToggleView = () => this.setState(({ view }) => ({ view: view === 'month' ? 'year' : 'month' }))

    onNextClicked = () => {
      if (!this.hideNext()) {
        this.setState(({ view }) => ({
          firstDate: moment(this.state.firstDate).add(view === 'month' ? 1 : 12, 'months'),
        }))
      }
    }

    onPreviousClicked = () => {
      if (!this.hidePrevious()) {
        this.setState(({ view }) => ({
          firstDate: moment(this.state.firstDate).subtract(view === 'month' ? 1 : 12, 'months'),
        }))
      }
    }

    onDateSelected = (e: React.SyntheticEvent) => {
      this.props.onDateSelected((e.currentTarget as any).dataset.formattedDate)
    }

    onMonthSelected = (e: React.SyntheticEvent) => {
      this.setState({ view: 'month', firstDate: moment((e.currentTarget as any).dataset.formattedDate) })
    }

    onMonthTitleClicked = () => {
      const date = this.getSelectedDate()
      if (date) {
        this.setState({
          firstDate: date.startOf('month'),
        })
      }
    }

    getSelectedDateString() {
      const date = this.props.isFromMonth ? this.props.timeperiod.fromDate : this.props.timeperiod.toDate
      if (!date) return ''
      return getFullDate(this.props.locale)(date.toDate())
    }

    getSelectedDate() {
      if (this.props.isFromMonth) {
        return this.props.timeperiod.fromDate && this.props.timeperiod.fromDate.clone()
      }
      return this.props.timeperiod.toDate && this.props.timeperiod.toDate.clone()
    }

    getDayLabels() {
      const daylabel = {
        fontWeight: '700',
        width: '40px',
        height: '40px',
        lineHeight: '40px',
        color: '#999999',
        textAlign: 'center',
      }
      return range(7).map(i => {
        return (
          <div {...css(daylabel)} key={`${this.props.isFromMonth}-weekday-${i}`}>
            {moment()
              .weekday(i)
              .format('dd')}
          </div>
        )
      })
    }

    getMonths() {
      const date = moment(this.state.firstDate).startOf('year')
      const items = []

      for (const i of range(0, 12)) {
        items.push(
          this.getDatepickerMonth({
            date: moment(date)
              .add(i, 'months')
              .startOf('month'),
          })
        )
      }

      return items
    }

    getDates() {
      const date = moment(this.state.firstDate)
      let items = this.getLastMonthDates(date, [])
      items = this.getThisMonthDates(date, items)
      items = this.getNextMonthDates(date, items)
      return items
    }

    getLastMonthDates(date: moment.Moment, items: React.ReactNode[]) {
      const firstWeekday = moment(date).weekday() + 1
      if (firstWeekday === 1) return items
      for (const i of range(1, firstWeekday)) {
        items.push(
          this.getDatepickerDate({
            date: moment(date)
              .subtract(firstWeekday - i, 'days')
              .startOf('day'),
            isOtherMonth: true,
          })
        )
      }
      return items
    }

    getNextMonthDates(d: moment.Moment, items: React.ReactNode[]) {
      const date = moment(d)
        .endOf('month')
        .startOf('day')
      const lastWeekday = date.weekday()
      if (lastWeekday === 6) return items
      for (const i of range(1, 7 - lastWeekday)) {
        items.push(
          this.getDatepickerDate({
            date: moment(date)
              .add(i, 'days')
              .startOf('day'),
            isOtherMonth: true,
          })
        )
      }
      return items
    }

    getThisMonthDates(date: moment.Moment, items: React.ReactNode[]) {
      for (const i of range(1, date.daysInMonth() + 1)) {
        const tempDate = moment(date).startOf('day')
        tempDate.set('date', i)
        items.push(this.getDatepickerDate({ date: tempDate }))
      }
      return items
    }

    getDatepickerDate(props: { date: moment.Moment; isOtherMonth?: boolean }) {
      return (
        <DatepickerDate
          {...props}
          firstSelectableDate={this.props.firstSelectableDate}
          isEmptyPeriod={isEmptyPeriod(this.props)}
          isFromMonth={this.props.isFromMonth}
          isSingleMonth={this.props.isSingleMonth}
          key={`${this.props.isFromMonth ? 'start' : ''}-date-${props.date && props.date.format('YYYY-MM-DD')}`}
          lastSelectableDate={this.props.lastSelectableDate}
          onDateSelected={this.onDateSelected}
          timeperiod={this.props.timeperiod}
        />
      )
    }

    getDatepickerMonth(props: { date: moment.Moment }) {
      return (
        <DatepickerDate
          {...props}
          firstSelectableDate={
            this.props.firstSelectableDate && moment(this.props.firstSelectableDate).startOf('month')
          }
          format="MMM"
          isEmptyPeriod={isEmptyPeriod(this.props)}
          isFromMonth={this.props.isFromMonth}
          isSingleMonth={this.props.isSingleMonth}
          key={`${this.props.isFromMonth ? 'start' : ''}-date-${props.date && props.date.format('YYYY-MM-DD')}`}
          lastSelectableDate={this.props.lastSelectableDate && moment(this.props.lastSelectableDate).startOf('month')}
          onDateSelected={this.onMonthSelected}
          timeperiod={{
            fromDate: this.state.firstDate,
            toDate: this.state.firstDate,
          }}
        />
      )
    }

    getCaption() {
      return this.state.view === 'month'
        ? getFullMonth(this.props.locale)(this.state.firstDate.toDate())
        : this.state.view === 'year'
        ? getFullYear(this.props.locale)(this.state.firstDate.toDate())
        : null
    }

    hideNext() {
      const lastOfMonth = moment(this.state.firstDate).endOf('month')
      const afterLastSelectable =
        (this.props.lastSelectableDate && this.props.lastSelectableDate.isBefore(lastOfMonth)) || false

      if (this.props.isFromMonth && !isEmptyPeriod(this.props)) {
        if (!this.props.timeperiod.toDate) return afterLastSelectable
        return this.props.timeperiod.toDate.isBefore(lastOfMonth)
      }
      return afterLastSelectable
    }

    hidePrevious() {
      const firstOfMonth = this.state.firstDate.startOf('month')
      const beforeFirstSelectable =
        (this.props.firstSelectableDate &&
          (this.props.firstSelectableDate.isAfter(firstOfMonth) ||
            this.props.firstSelectableDate.isSame(firstOfMonth))) ||
        false
      if (this.props.isFromMonth || this.props.isSingleMonth || isEmptyPeriod(this.props)) {
        return beforeFirstSelectable
      }

      if (!this.props.timeperiod.fromDate) return beforeFirstSelectable

      return (
        this.props.timeperiod.fromDate.isAfter(firstOfMonth) ||
        this.props.timeperiod.fromDate.isSame(firstOfMonth, 'day')
      )
    }

    render() {
      const styles = getStyles(this.props.theme)
      return (
        <Flex
          {...css(
            {
              opacity: '1',
              width: '280px',
              height: 'auto',
              paddingBottom: '20px',
              userSelect: 'none',
            },
            this.props.style
          )}
        >
          <Flex alignItems="center" justifyContent="center" row>
            <StyledText {...css({ textAlign: 'center' })} variant="dim" weight="600">
              {this.props.title}
            </StyledText>
            <Touchable
              {...css(styles.title, styles.dateClass)}
              data-test="selected-date-title"
              onClick={this.onMonthTitleClicked}
            >
              {this.getSelectedDateString()}
            </Touchable>
          </Flex>{' '}
          <Flex justifyContent="space-between" row {...css({ margin: '5px 0' })} alignItems="center">
            <Touchable
              {...css([styles.arrow, this.hidePrevious() && styles.arrowDisabled])}
              data-prev
              disabled={this.hidePrevious()}
              onClick={this.onPreviousClicked}
            >
              <ArrowRight width="20px" {...css({ transform: 'rotate(180deg)' })} />
            </Touchable>
            <Touchable {...css(styles.monthName)} component="h4" onClick={this.onToggleView}>
              <span {...css(styles.monthNameInner)}>{this.getCaption()}</span>
              <DoubleArrow {...css(styles.monthNameDoubleArrow)} />
            </Touchable>
            <Touchable
              {...css([styles.arrow, this.hideNext() && styles.arrowDisabled])}
              data-next
              disabled={this.hideNext()}
              onClick={this.onNextClicked}
            >
              <ArrowRight width="20px" />
            </Touchable>
          </Flex>
          {this.state.view === 'month' ? (
            <div {...css(styles.dates)}>
              {this.getDayLabels()}
              {this.getDates()}
            </div>
          ) : (
            <div {...css(styles.months)}>{this.getMonths()}</div>
          )}
        </Flex>
      )
    }
  }
)
